import { forwardRef, type MouseEvent, type ReactNode } from "react";
import { Box, type SxProps, type Theme } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

interface HeaderTriggerButtonProps {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  "aria-label": string;
  "aria-haspopup"?: "menu" | "dialog" | "listbox" | true;
  "aria-expanded"?: boolean;
  /** Text colour token; defaults to text.primary. */
  color?: string;
  sx?: SxProps<Theme>;
}

/**
 * The review header's value trigger (#7431, artboard 1a): 13px/600 text with a
 * chevron, transparent border, hover tint. One component so Client, Category,
 * Paid through and Layout look and behave alike — the menu/popover each one
 * opens stays with its owner.
 */
export const HeaderTriggerButton = forwardRef<HTMLButtonElement, HeaderTriggerButtonProps>(
  ({ children, onClick, disabled = false, color = "text.primary", sx, ...aria }, ref) => (
    <Box
      ref={ref}
      component="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={aria["aria-label"]}
      aria-haspopup={aria["aria-haspopup"]}
      aria-expanded={aria["aria-expanded"] ? "true" : undefined}
      sx={{
        minHeight: 28,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        border: "1px solid transparent",
        bgcolor: "transparent",
        borderRadius: 1.5,
        font: "inherit",
        fontSize: 13,
        fontWeight: 600,
        color,
        cursor: disabled ? "default" : "pointer",
        whiteSpace: "nowrap",
        "&:hover:not(:disabled)": { bgcolor: "action.hover", borderColor: "divider" },
        ...sx,
      }}
    >
      {children}
      <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
    </Box>
  )
);
HeaderTriggerButton.displayName = "HeaderTriggerButton";
